import { motion } from 'framer-motion';
import { useState } from 'react';
import {
  DollarSign,
  Building,
  TrendingUp,
  Cube,
  Globe,
  Beaker,
  Cpu,
  Truck,
  ShoppingBag,
  Sun,
  Heart,
  Wrench,
  Rocket,
  CircleDollarSign,
  Smartphone,
  Cloud,
  BarChart3,
  Target,
  Zap
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

function Portfolio() {
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  const toneStyles = {
    blue: { chip: 'bg-blue-500/15 text-blue-200 ring-blue-400/20', dot: 'bg-blue-400' },
    green: { chip: 'bg-emerald-500/15 text-emerald-200 ring-emerald-400/20', dot: 'bg-emerald-400' },
    purple: { chip: 'bg-violet-500/15 text-violet-200 ring-violet-400/20', dot: 'bg-violet-400' },
    red: { chip: 'bg-rose-500/15 text-rose-200 ring-rose-400/20', dot: 'bg-rose-400' },
    yellow: { chip: 'bg-amber-500/15 text-amber-200 ring-amber-400/20', dot: 'bg-amber-400' },
    cyan: { chip: 'bg-cyan-500/15 text-cyan-200 ring-cyan-400/20', dot: 'bg-cyan-400' },
    pink: { chip: 'bg-pink-500/15 text-pink-200 ring-pink-400/20', dot: 'bg-pink-400' },
    indigo: { chip: 'bg-indigo-500/15 text-indigo-200 ring-indigo-400/20', dot: 'bg-indigo-400' },
    orange: { chip: 'bg-orange-500/15 text-orange-200 ring-orange-400/20', dot: 'bg-orange-400' },
  };

  const industries = [
    {
      id: 'tech',
      name: 'Technology',
      icon: Cpu,
      percentage: 25,
      description: 'Leading tech companies and emerging startups in AI, cloud computing, and cybersecurity.',
      companies: ['Artificial Intelligence', 'Cloud Services', 'Cybersecurity', '5G Networks'],
      color: 'blue',
      growth: '+18.5%'
    },
    {
      id: 'finance',
      name: 'Financial Services',
      icon: DollarSign,
      percentage: 20,
      description: 'Digital payment solutions, fintech innovations, and blockchain technology.',
      companies: ['Digital Banking', 'Payment Processing', 'Blockchain', 'Insurance Tech'],
      color: 'green',
      growth: '+15.2%'
    },
    {
      id: 'realestate',
      name: 'Real Estate',
      icon: Building,
      percentage: 15,
      description: 'Premium commercial properties, residential developments, and REITs.',
      companies: ['Commercial Properties', 'Residential Developments', 'Data Centers', 'Industrial Facilities'],
      color: 'purple',
      growth: '+12.8%'
    },
    {
      id: 'healthcare',
      name: 'Healthcare',
      icon: Heart,
      percentage: 10,
      description: 'Biotechnology, medical devices, and healthcare technology solutions.',
      companies: ['Biotech Research', 'Medical Devices', 'Digital Health', 'Pharmaceuticals'],
      color: 'red',
      growth: '+22.1%'
    },
    {
      id: 'energy',
      name: 'Clean Energy',
      icon: Sun,
      percentage: 10,
      description: 'Renewable energy projects, sustainable technologies, and clean energy infrastructure.',
      companies: ['Solar Power', 'Wind Energy', 'Energy Storage', 'Smart Grid'],
      color: 'yellow',
      growth: '+28.7%'
    },
    {
      id: 'manufacturing',
      name: 'Advanced Manufacturing',
      icon: Wrench,
      percentage: 5,
      description: '3D printing, robotics, and smart manufacturing solutions.',
      companies: ['3D Printing', 'Robotics', 'IoT Manufacturing', 'Smart Factories'],
      color: 'cyan',
      growth: '+16.3%'
    },
    {
      id: 'ecommerce',
      name: 'E-Commerce',
      icon: ShoppingBag,
      percentage: 5,
      description: 'Digital retail platforms, logistics, and e-commerce technology.',
      companies: ['Online Retail', 'Digital Marketplaces', 'Logistics Tech', 'Payment Solutions'],
      color: 'pink',
      growth: '+19.4%'
    },
    {
      id: 'logistics',
      name: 'Transportation & Logistics',
      icon: Truck,
      percentage: 5,
      description: 'Electric vehicles, autonomous transportation, and smart logistics.',
      companies: ['Electric Vehicles', 'Autonomous Tech', 'Smart Logistics', 'Fleet Management'],
      color: 'indigo',
      growth: '+14.6%'
    },
    {
      id: 'crypto',
      name: 'Cryptocurrency',
      icon: CircleDollarSign,
      percentage: 5,
      description: 'Digital assets, DeFi protocols, and blockchain infrastructure.',
      companies: ['Bitcoin', 'Ethereum', 'DeFi Protocols', 'NFT Platforms'],
      color: 'orange',
      growth: '+35.2%'
    }
  ];

  const portfolioStats = [
    { value: '$2.8B', label: 'Total Portfolio Value', icon: DollarSign },
    { value: '94%', label: 'Success Rate', icon: Target },
    { value: '+24.5%', label: 'Average Annual Return', icon: TrendingUp },
    { value: '150+', label: 'Active Investments', icon: Building }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <Navigation />

      {/* Hero Section - Unique Design */}
      <section className="relative border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_0%,rgba(34,211,238,0.14),transparent_55%),radial-gradient(800px_circle_at_90%_20%,rgba(56,189,248,0.10),transparent_55%)]" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto py-16 md:py-20">
            <motion.div
              className="inline-flex items-center gap-2 mb-6 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold tracking-wide text-stone-200"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
              Research-led portfolio construction
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-semibold mb-6 leading-[1.05] tracking-tight"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <span className="text-white">A diversified investment</span>
              <span className="block text-cyan-200">portfolio built for resilience</span>
            </motion.h1>
            
            <motion.p 
              className="text-base md:text-lg mb-10 text-slate-300 leading-relaxed max-w-3xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              We organize capital across sectors and themes so portfolios can pursue growth while staying anchored to diversification, liquidity needs, and risk controls you can monitor over time.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 justify-center"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <Link to="/investment-plans">
                <button className="w-full sm:w-auto rounded-xl bg-cyan-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors">
                  Explore investments
                </button>
              </Link>
              <Link to="/contact">
                <button className="w-full sm:w-auto rounded-xl border border-white/15 bg-white/5 px-8 py-3 text-sm font-semibold text-stone-100 hover:bg-white/10 transition-colors">
                  Talk to the team
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Portfolio Stats */}
      <section className="py-16 md:py-20 bg-stone-950">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {portfolioStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center rounded-2xl border border-white/10 bg-stone-950/40 p-8 card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.5 }}
                >
                  <Icon className="mx-auto mb-4 text-cyan-300" size={44} />
                  <div className="text-3xl font-semibold mb-2 tabular-nums tracking-tight">{stat.value}</div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Allocation */}
      <section className="py-16 md:py-20 border-t border-white/10">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight text-white">
              Industry allocation
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
              Strategic distribution of assets across sectors positioned for long-term growth and stability
            </p>
          </div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {industries.map((industry) => {
              const Icon = industry.icon;
              const tone = toneStyles[industry.color] || toneStyles.blue;
              return (
              <motion.div
                key={industry.id}
                  className="card-glass p-8 rounded-2xl card-hover cursor-pointer"
                variants={itemVariants}
                whileHover={{ y: -4 }}
                onClick={() => setSelectedIndustry(industry)}
              >
                  <div className="flex items-center justify-between mb-6">
                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ring-1 ${tone.chip}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-white">{industry.percentage}%</div>
                      <div className="text-sm text-emerald-300 tabular-nums">{industry.growth}</div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">{industry.name}</h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">{industry.description}</p>

                  <div className="space-y-2">
                    {industry.companies.slice(0, 3).map((company, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className={`h-2 w-2 rounded-full ${tone.dot}`} />
                        <span className="text-sm text-slate-400">{company}</span>
                      </div>
                    ))}
                    {industry.companies.length > 3 && (
                      <div className="text-sm text-cyan-200 font-medium">
                        +{industry.companies.length - 3} more
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Investment Strategy */}
      <section className="py-16 md:py-20 bg-stone-950 border-t border-white/10">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-6 tracking-tight text-white">
                Our investment strategy
              </h2>
              <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed">
                We employ a sophisticated multi-strategy approach that combines fundamental analysis, technical insights, and market timing to maximize returns while managing risk.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-cyan-400/25 bg-cyan-400/10 text-sm font-semibold text-cyan-100">
                    01
                    </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Diversification</h3>
                    <p className="text-slate-300">Spread investments across multiple sectors and asset classes to reduce risk.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-cyan-400/25 bg-cyan-400/10 text-sm font-semibold text-cyan-100">
                    02
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Active Management</h3>
                    <p className="text-slate-300">Continuous monitoring and adjustment based on market conditions and opportunities.</p>
          </div>
        </div>

                <div className="flex items-start space-x-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-cyan-400/25 bg-cyan-400/10 text-sm font-semibold text-cyan-100">
                    03
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Risk Management</h3>
                    <p className="text-slate-300">Comprehensive risk assessment and mitigation strategies for capital preservation.</p>
        </div>
          </div>
          </div>
        </div>

            <div className="card-glass p-8 rounded-2xl">
              <h3 className="text-2xl font-semibold text-white mb-6 tracking-tight">Performance highlights</h3>
              
              <div className="space-y-6">
                <div className="flex justify-between items-center rounded-xl border border-white/10 bg-white/5 p-4">
                  <div>
                    <div className="text-white font-semibold">Total Return</div>
                    <div className="text-slate-400 text-sm">Last 12 months</div>
                  </div>
                  <div className="text-2xl font-semibold text-emerald-300 tabular-nums">+24.5%</div>
                </div>

                <div className="flex justify-between items-center rounded-xl border border-white/10 bg-white/5 p-4">
                  <div>
                    <div className="text-white font-semibold">Sharpe Ratio</div>
                    <div className="text-slate-400 text-sm">Risk-adjusted return</div>
                  </div>
                  <div className="text-2xl font-semibold text-cyan-200 tabular-nums">1.85</div>
                </div>

                <div className="flex justify-between items-center rounded-xl border border-white/10 bg-white/5 p-4">
                  <div>
                    <div className="text-white font-semibold">Max Drawdown</div>
                    <div className="text-slate-400 text-sm">Peak to trough</div>
                  </div>
                  <div className="text-2xl font-semibold text-rose-300 tabular-nums">-8.2%</div>
                </div>

                <div className="flex justify-between items-center rounded-xl border border-white/10 bg-white/5 p-4">
                  <div>
                    <div className="text-white font-semibold">Win Rate</div>
                    <div className="text-slate-400 text-sm">Successful trades</div>
          </div>
                  <div className="text-2xl font-semibold text-violet-200 tabular-nums">94%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 border-t border-white/10 bg-stone-950">
        <div className="container mx-auto px-6 text-center">
          <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-white/5 p-10">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight text-white">Invest alongside our portfolio mandate</h2>
            <p className="text-base md:text-lg text-slate-300 mb-8">
              Explore plans, compare minimums, and start onboarding in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/investment-plans">
              <button className="w-full sm:w-auto rounded-xl bg-cyan-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors">
                View investment plans
              </button>
            </Link>
              <Link to="/contact">
              <button className="w-full sm:w-auto rounded-xl border border-white/15 bg-white/5 px-8 py-3 text-sm font-semibold text-stone-100 hover:bg-white/10 transition-colors">
                Contact our team
                </button>
              </Link>
          </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Portfolio;